(() => {
  if (window.shcSubtleMotion) return;
  window.shcSubtleMotion = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 749px)');
  const active = new Set();
  const seen = new Set();
  const pending = new Map();
  const earlyGroups = '.lp-delivery__benefits,.summary-portrait__benefits,.problem-story__list,.lp-gentler__exclusions';
  const countTargets = '.survey-panorama__copy>strong,.results-portrait__number';
  let motionFailed = false;
  function recover() {
    motionFailed = true;
    active.forEach(finish => { try { finish(); } catch {} });
    pending.forEach((_, el) => show(el));
  }
  function prepare(el) {
    if (motionFailed || reduced.matches || !Element.prototype.animate) return;
    let parts = [el];
    if (el.matches('.lp-reusable__comparison,.lp-delivery__benefits,.summary-portrait__benefits,.problem-story__list,.lp-gentler__exclusions')) {
      parts = [...el.children];
    }
    if (el.matches('.tech-explainer__callout')) {
      parts = [...el.querySelectorAll('.tech-explainer__line,h3,p')];
    } else if (el.matches('.ingredient-callout__label')) {
      const section = el.closest('section');
      const index = [...section.querySelectorAll('.ingredient-callout__label')].indexOf(el);
      parts.push(...[...section.querySelectorAll('.ingredient-callout__lines')]
        .map(svg => svg.querySelectorAll('path')[index]).filter(Boolean));
    }
    pending.set(el, parts.map(part => [part, part.style.getPropertyValue('opacity'), part.style.getPropertyPriority('opacity')]));
    parts.forEach(part => part.style.opacity = '0');
  }
  function show(el) {
    const parts = pending.get(el);
    if (!parts) return;
    parts.forEach(([part, value, priority]) => {
      if (value) part.style.setProperty('opacity', value, priority);
      else part.style.removeProperty('opacity');
    });
    pending.delete(el);
  }
  // Replay on reload, but only once per element while this page stays open.
  const wasSeen = el => seen.has(el);
  const remember = el => seen.add(el);
  function animate(el, frames, duration, delay = 0) {
    const animation = el.animate(frames, { duration, delay, fill: 'backwards', easing: 'cubic-bezier(.2,.65,.3,1)' });
    const finish = () => animation.cancel();
    active.add(finish);
    animation.finished.catch(() => {}).finally(() => active.delete(finish));
  }
  function reveal(el, delay) {
    if (el.matches('.lp-reusable__comparison,.lp-delivery__benefits,.summary-portrait__benefits,.problem-story__list,.lp-gentler__exclusions')) {
      const step = el.matches('.lp-reusable__comparison') ? 280 : 100;
      [...el.children].forEach((part, index) => animate(part,
        [{ opacity: 0, translate: '0 10px' }, { opacity: 1, translate: '0 0' }],
        750, delay + index * step));
    } else if (el.matches('.tech-explainer__callout')) {
      const line = el.querySelector('.tech-explainer__line');
      const image = el.closest('section').querySelector('.tech-explainer__image');
      if (line && image) {
        const r = line.getBoundingClientRect(), c = image.getBoundingClientRect();
        const origin = r.left + r.width / 2 < c.left + c.width / 2 ? 'right center' : 'left center';
        animate(line, [{ transform: 'scaleX(0)', transformOrigin: origin }, { transform: 'scaleX(1)', transformOrigin: origin }], 650, delay);
      }
      el.querySelectorAll('h3,p').forEach(text => animate(text, [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'translateY(0)' }], 750, delay + 160));
    } else if (el.matches('.ingredient-callout__label')) {
      const section = el.closest('section');
      const index = [...section.querySelectorAll('.ingredient-callout__label')].indexOf(el);
      section.querySelectorAll('.ingredient-callout__lines').forEach(svg => {
        const line = svg.querySelectorAll('path')[index];
        if (!line || !svg.getBoundingClientRect().width) return;
        // Non-scaling strokes measure dashes in screen units, not viewBox units.
        const matrix = line.getScreenCTM();
        const scale = matrix ? Math.hypot(matrix.a, matrix.b) : 1;
        const length = line.getTotalLength() * scale;
        animate(line, [
          { strokeDasharray: `${length}`, strokeDashoffset: -length },
          { strokeDasharray: `${length}`, strokeDashoffset: 0 }
        ], 700, delay);
      });
      // Individual translate preserves the label's existing centering transform.
      animate(el, [{ opacity: 0, translate: '0 8px' }, { opacity: 1, translate: '0 0' }], 750, delay + 120);
    } else {
      animate(el, [{ opacity: 0, translate: '0 12px' }, { opacity: 1, translate: '0 0' }], 850, delay);
    }
  }
  function count(el) {
    const final = el.textContent.trim();
    if (!/^\d+%?$/.test(final)) return;
    const suffix = final.endsWith('%') ? '%' : '';
    // Keep the exact accessible text and its width; animate an aria-hidden overlay.
    const originalStyle = el.getAttribute('style');
    const ink = getComputedStyle(el).color;
    const overlay = document.createElement('span');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = `position:absolute;inset:0;color:${ink};font:inherit;letter-spacing:inherit;pointer-events:none`;
    el.style.position = 'relative';
    el.style.color = 'transparent';
    el.append(overlay);
    const target = parseInt(final, 10), start = performance.now();
    let frame;
    const finish = () => {
      cancelAnimationFrame(frame);
      overlay.remove();
      if (originalStyle === null) el.removeAttribute('style'); else el.setAttribute('style', originalStyle);
      active.delete(finish);
    };
    active.add(finish);
    const tick = now => {
      const t = Math.min((now - start) / 700, 1);
      overlay.textContent = `${Math.round(target * (1 - Math.pow(1 - t, 3)))}${suffix}`;
      if (t < 1 && !reduced.matches && !document.hidden) frame = requestAnimationFrame(tick); else finish();
    };
    tick(start);
  }
  const onIntersect = entries => {
    let stagger = 0;
    try { entries.forEach(entry => {
      const el = entry.target;
      if (!entry.isIntersecting || (!el.matches(earlyGroups) && entry.intersectionRatio < .35)) return;
      observer.unobserve(el);
      surveyObserver.unobserve(el);
      earlyObserver.unobserve(el);
      if (wasSeen(el)) return;
      remember(el);
      if (motionFailed || reduced.matches || document.hidden || !Element.prototype.animate) { show(el); return; }
      try {
        if (el.matches(countTargets)) count(el);
        else reveal(el, el.matches(earlyGroups) ? 0 : 100 + Math.min(stagger++ * (mobile.matches ? 100 : 150), 450));
      } finally {
        // Backwards fill now owns the hidden state through the stagger delay.
        show(el);
      }
    }); } catch { recover(); }
  };
  const observer = new IntersectionObserver(onIntersect, { threshold: .35, rootMargin: '0px 0px -80px 0px' });
  const surveyObserver = new IntersectionObserver(onIntersect, { threshold: .35 });
  const earlyObserver = new IntersectionObserver(onIntersect, { threshold: 0, rootMargin: `0px 0px -${Math.round(window.innerHeight * .25)}px 0px` });
  function init(root = document) {
    ['.tech-explainer__callout', '.survey-panorama__copy>strong', '.lp-planet li', '.ingredient-callout__label', '.lp-reusable__comparison', '.lp-delivery__photo', '.lp-delivery__benefits', '.summary-portrait__benefits', '.problem-story__list', '.lp-gentler__exclusions', '.results-portrait__number'].forEach(selector => {
      root.querySelectorAll(selector).forEach((el, index) => {
        if (el.dataset.motionKey) return;
        el.dataset.motionKey = `${selector}:${index}`;
        if (!el.matches(countTargets)) prepare(el);
        (el.matches(countTargets) ? surveyObserver : el.matches(earlyGroups) ? earlyObserver : observer).observe(el);
      });
    });
  }
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      active.forEach(finish => finish());
      pending.forEach((_, el) => show(el));
    }
  });
  document.addEventListener('visibilitychange', () => { if (document.hidden) active.forEach(finish => finish()); });
  function safeInit(root) {
    try { init(root); } catch { recover(); }
  }
  document.addEventListener('shopify:section:load', event => safeInit(event.target));
  safeInit(document);
})();
